import { LargeDashboardWrapper, Wrapper } from "./largeDashboard.styles";
import React, { useCallback, memo } from "react";
import { ButtonComponent } from "../../button/button.component";
import { NumericFormat } from "react-number-format";
import { IoEyeOutline, IoEyeOffOutline } from "react-icons/io5";
import { IoIosAddCircleOutline } from "react-icons/io";

const TransferIcon: React.FC = () => (
    <svg className={"me-2 icon"} viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
            d="M6.16689 5.26667L13.2419 2.90833C16.4169 1.85 18.1419 3.58333 17.0919 6.75833L14.7336 13.8333C13.1502 18.5917 10.5502 18.5917 8.96689 13.8333L8.26689 11.7333L6.16689 11.0333C1.40856 9.45 1.40856 6.85833 6.16689 5.26667Z"
            stroke="currentColor" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round"
        />
        <path
            d="M8.4248 11.375L11.4081 8.38333"
            stroke="currentColor" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round"
        />
    </svg>
);

interface LargeDashboardComponentProps {
    hideBalance?: boolean;
    balance: string | number;
    toggler: () => void;
    onOpenChange: (open: boolean) => void;
    setActiveService: React.Dispatch<React.SetStateAction<string | null>>;
}

const BalanceDisplay: React.FC<{ balance: string | number; hideBalance: boolean }> = memo(({
                                                                                               balance,
                                                                                               hideBalance
                                                                                           }) => {
    if (hideBalance) {
        return <h1>****</h1>;
    }

    return (
        <h1>
            <NumericFormat
                value={balance}
                displayType="text"
                thousandSeparator=","
                decimalScale={2}
                fixedDecimalScale
                prefix="₦"
            />
        </h1>
    );
});

// Balance header component
const BalanceHeader: React.FC<{ hideBalance: boolean; toggler: () => void }> = memo(({
                                                                                         hideBalance,
                                                                                         toggler
                                                                                     }) => (
    <div className="title">
        <h6 className="title !mb-0">Total Balance</h6>
        <div onClick={toggler} className="icon-btn" role="button" tabIndex={0}>
            {hideBalance ? (
                <IoEyeOutline strokeWidth={2} stroke="currentColor" />
            ) : (
                <IoEyeOffOutline strokeWidth={2} stroke="currentColor" />
            )}
        </div>
    </div>
));

// Action button component
const DashboardActionButton: React.FC<{
    service: string;
    onOpenChange: (open: boolean) => void;
    setActiveService: (service: string) => void;
    icon: React.ReactNode;
    children: React.ReactNode;
    className?: string;
}> = memo(({ service, onOpenChange, setActiveService, icon, children, className }) => {
    const handleClick = useCallback(() => {
        setActiveService(service);
        onOpenChange(true);
    }, [service, onOpenChange, setActiveService]);

    return (
        <ButtonComponent
            className={className}
            onClick={handleClick}
            fullWidth={true}
            size="base"
            icon={icon}
        >
            {children}
        </ButtonComponent>
    );
});

export const LargeDashboardComponent = memo(({
                                                 balance,
                                                 onOpenChange,
                                                 setActiveService,
                                                 toggler,
                                                 hideBalance = true
                                             }: LargeDashboardComponentProps) => {

    // Memoized button handlers to prevent unnecessary re-renders
    const handleAddMoney = useCallback(() => {
        setActiveService("FundWallet");
        onOpenChange(true);
    }, [setActiveService, onOpenChange]);

    const handleTransfer = useCallback(() => {
        setActiveService("Transfer");
        onOpenChange(true);
    }, [setActiveService, onOpenChange]);

    return (
        <Wrapper>
            <LargeDashboardWrapper>
                <BalanceHeader hideBalance={hideBalance} toggler={toggler} />
                <div className="balance">
                    <BalanceDisplay balance={balance} hideBalance={hideBalance} />
                </div>
            </LargeDashboardWrapper>

            <div className="dashboard-action">
                <DashboardActionButton
                    service="FundWallet"
                    onOpenChange={onOpenChange}
                    setActiveService={setActiveService}
                    icon={<IoIosAddCircleOutline  className={"me-2 "} />}
                    className="outline-tablet"
                >
                    Add Money
                </DashboardActionButton>

                <DashboardActionButton
                    service="Transfer"
                    onOpenChange={onOpenChange}
                    setActiveService={setActiveService}
                    icon={<TransferIcon />}
                >
                    Transfer
                </DashboardActionButton>
            </div>
        </Wrapper>
    );
});

LargeDashboardComponent.displayName = "LargeDashboardComponent";